import { useCallback, useEffect, useState } from 'react';
import { clearSession, readSession, SESSION_CHANGED, type Session } from '@/shared/api/session';

/**
 * The current session, kept in sync with storage.
 *
 * Subscribes to the `SESSION_CHANGED` event rather than reading storage on render,
 * because a session can end **inside an axios interceptor** — a failed token refresh is
 * not a user action and is not tied to any component. Without the subscription the panel
 * would keep rendering an authenticated shell while every request returned 401.
 *
 * `storage` is listened to as well, so signing out in one tab signs out the others. An
 * operations panel is routinely open in several tabs.
 */
export function useSession(): { session: Session | null; signOut: () => void } {
  const [session, setSession] = useState<Session | null>(readSession);

  useEffect(() => {
    const sync = () => setSession(readSession());

    window.addEventListener(SESSION_CHANGED, sync);
    window.addEventListener('storage', sync);

    return () => {
      window.removeEventListener(SESSION_CHANGED, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const signOut = useCallback(() => clearSession(), []);

  return { session, signOut };
}
